import React, { useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./CancelAppointmentModal.module.css";
import { IAppointment } from "../../types/appointments.types";
import {
  useGetCancelAppReasonsQuery,
  useCancelAppointmentMutation,
} from "../../../../services/api/utils.api";

interface ICancelAppointmentModalProps {
  appointment: IAppointment;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Cancel Appointment Modal Component
 *
 * Allows users to cancel an appointment by selecting a reason.
 * Shows confirmation before canceling and sends notification to therapist.
 */
const CancelAppointmentModal: React.FC<ICancelAppointmentModalProps> = ({
  appointment,
  onClose,
  onSuccess,
}) => {
  const [selectedReasonId, setSelectedReasonId] = useState<number | null>(null);
  const [otherReason, setOtherReason] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);

  const { data: cancelReasons, isLoading: isLoadingReasons } =
    useGetCancelAppReasonsQuery();

  const [cancelAppointment, { isLoading: isCanceling }] =
    useCancelAppointmentMutation();

  const selectedReason = cancelReasons?.find((r) => r.id === selectedReasonId);
  const isOtherReason = selectedReason?.isAnother ?? false;
  const canSubmit =
    selectedReasonId !== null && (!isOtherReason || otherReason.trim());

  // Set direction based on language
  const isRtl = i18n.language === "he";

  const handleCancel = async () => {
    if (!canSubmit || !selectedReasonId) return;

    try {
      await cancelAppointment({
        appointmentsId: appointment.id,
        cancelAppReasonsId: selectedReasonId,
        anotherReason: isOtherReason ? otherReason : "",
      }).unwrap();

      onSuccess();
    } catch (error) {
      console.error("Failed to cancel appointment:", error);
      // Could show error toast here
    }
  };

  const formatDateTime = () => {
    const date = new Date(appointment.date);
    const dateStr = date.toLocaleDateString(
      i18n.language === 'he' ? "he-IL" : "en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    return `${dateStr} | ${appointment.time}`;
  };

  if (isConfirming) {
    return (
      <div
        className={styles.modalOverlay}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>
              <span>⚠️</span>
              <span>{t("APPOINTMENTS_CANCEL_CONFIRM_TITLE")}</span>
            </div>
            <button className={styles.closeBtn} onClick={onClose}>
              ×
            </button>
          </div>

          <div className={styles.modalBody}>
            <div className={styles.confirmMessage}>
              <p className={styles.confirmText}>
                {t("APPOINTMENTS_CANCEL_CONFIRM_MESSAGE")}
              </p>
              <div className={styles.appointmentSummary}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryIcon}>👤</span>
                  <span>
                    {appointment.patientName || appointment.treatmentGroupName}
                  </span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryIcon}>👨‍⚕️</span>
                  <span>{appointment.therapistName}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryIcon}>📅</span>
                  <span>{formatDateTime()}</span>
                </div>
              </div>
              <div className={styles.reasonSummary}>
                <strong>{t("APPOINTMENTS_CANCEL_REASON")}:</strong>{" "}
                {selectedReason?.reason}
                {isOtherReason && otherReason && ` - ${otherReason}`}
              </div>
              <p className={styles.notificationNote}>
                📧 {t("APPOINTMENTS_CANCEL_NOTIFICATION_NOTE")}
              </p>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button
              className={styles.backBtn}
              onClick={() => setIsConfirming(false)}
            >
              {t("APPOINTMENTS_BACK")}
            </button>
            <button
              className={styles.confirmCancelBtn}
              onClick={handleCancel}
              disabled={isCanceling}
            >
              {isCanceling
                ? t("APPOINTMENTS_CANCELING")
                : t("APPOINTMENTS_CONFIRM_CANCEL")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal} dir={isRtl ? "rtl" : "ltr"}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <span>🚫</span>
            <span>{t("APPOINTMENTS_CANCEL_TITLE")}</span>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* Appointment Info */}
          <div className={styles.appointmentInfo}>
            <div className={styles.infoRow}>
              <span className={styles.infoIcon}>👤</span>
              <span className={styles.infoLabel}>
                {t("APPOINTMENTS_PATIENT")}:
              </span>
              <span className={styles.infoValue}>
                {appointment.patientName || appointment.treatmentGroupName}
              </span>
            </div>
            <div className={styles.infoRow}>
              <span className={styles.infoIcon}>📅</span>
              <span className={styles.infoLabel}>
                {t("APPOINTMENTS_DATE_TIME")}:
              </span>
              <span className={styles.infoValue}>{formatDateTime()}</span>
            </div>
          </div>

          {/* Reason Selection */}
          <div className={styles.reasonSection}>
            <h3 className={styles.reasonTitle}>
              {t("APPOINTMENTS_SELECT_CANCEL_REASON")}
            </h3>
            {isLoadingReasons ? (
              <div className={styles.loading}>{t("LOADING")}</div>
            ) : (
              <div className={styles.reasonsList}>
                {cancelReasons?.map((reason) => (
                  <label
                    key={reason.id}
                    className={`${styles.reasonOption} ${
                      selectedReasonId === reason.id ? styles.selected : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="cancelReason"
                      value={reason.id}
                      checked={selectedReasonId === reason.id}
                      onChange={() => setSelectedReasonId(reason.id)}
                    />
                    <span className={styles.reasonText}>{reason.reason}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Other reason text input */}
            {isOtherReason && (
              <div className={styles.otherReasonInput}>
                <textarea
                  placeholder={t("APPOINTMENTS_ENTER_CANCEL_REASON")}
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  className={styles.textarea}
                  rows={3}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>
            {t("APPOINTMENTS_CLOSE")}
          </button>
          <button
            className={styles.submitBtn}
            onClick={() => setIsConfirming(true)}
            disabled={!canSubmit}
          >
            {t("APPOINTMENTS_CONTINUE")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CancelAppointmentModal;
